import { GetterTree } from 'vuex';
import numeral from 'numeral';
import { IInventoryState, IWallet, IFiatBalances, IClaimFee } from './types';
import { IRootState } from '../types';
import { IGamesState } from '../games/types';

export const getters: GetterTree<IInventoryState, IRootState> = {
  gyriTollBalance(state: IInventoryState): number {
    const tollWallet = state.wallets.find((wallet: IWallet) => {
      return wallet.symbol === 'GALA[GYRI]';
    });

    return Number(tollWallet?.balance?.confirmed ?? 0);
  },

  galaBalance(state: IInventoryState): number {
    const galaWallet = state.wallets.find((wallet: IWallet) => {
      return wallet.symbol === 'GALA[ETH]';
    });

    if (
      galaWallet &&
      galaWallet.balance &&
      galaWallet.balance.confirmed &&
      galaWallet.balance.unconfirmed
    ) {
      const { unconfirmed, confirmed } = galaWallet.balance;
      const lesserBalance = Math.min(+confirmed, +unconfirmed);

      return lesserBalance;
    }

    return 0;
  },

  getFiatBalances(state: IInventoryState): IFiatBalances {
    const { wallets } = state;
    const fiatBalances: IFiatBalances = { total: 0 };

    for (const wallet of wallets) {
      const { symbol, balance, receiveAddress, coinPrice } = wallet;
      let usdVal = 0;

      if (receiveAddress) {
        const lesserBalance = Math.min(
          +balance.confirmed,
          +balance.unconfirmed,
        );

        usdVal = coinPrice * lesserBalance;
      }

      fiatBalances[symbol] = usdVal;
      fiatBalances.total += usdVal;
    }

    return fiatBalances;
  },

  userItemsCount(state: IInventoryState) {
    const count = state.userItems.reduce((total, collection) => {
      return total + collection.quantity;
    }, 0);

    return count;
  },

  getCraftedItemPiecesNeededByGameRoute: (
    state: IInventoryState,
    inventoryGetters: any,
    rootState: IRootState,
  ) => ({ gameRoute, itemName }: { gameRoute: string; itemName: string }) => {
    const gameInfo = (rootState.games as IGamesState).games.find(
      game => game.route === gameRoute,
    );

    const craftedItem =
      gameInfo && gameInfo.craftedItems
        ? gameInfo.craftedItems.find(item => item.name === itemName)
        : undefined;

    if (gameInfo && craftedItem) {
      return craftedItem.requiredPieces.map(piece => {
        const userItem = state.userItems.find(
          item =>
            item.network === 'ETHEREUM' &&
            item.fungible &&
            item.ethereumBaseId === piece.baseId,
        );

        const pieceCount = userItem?.quantity ?? 0;
        const pieceWithQuantity = {
          ...piece,
          quantityOwned: pieceCount,
          sufficientInventory: pieceCount / piece.quantityNeeded >= 1,
        };

        return pieceWithQuantity;
      });
    }

    return [];
  },

  getCraftedItemProgressByGameRoute: (
    state: IInventoryState,
    gameGetters: any,
  ) => (payload: { gameRoute: string; itemName: string }) => {
    const craftedItemPiecesNeeded = gameGetters.getCraftedItemPiecesNeededByGameRoute(
      payload,
    );

    if (craftedItemPiecesNeeded && craftedItemPiecesNeeded.length) {
      const ratio = craftedItemPiecesNeeded.reduce(
        (a: any, b: any) => {
          a.quantityNeeded += b.quantityNeeded;
          a.quantityOwned += Math.min(b.quantityOwned, b.quantityNeeded);
          return a;
        },
        { quantityOwned: 0, quantityNeeded: 0 },
      );

      const percentage = (ratio.quantityOwned / ratio.quantityNeeded) * 100;

      return numeral(percentage).format('0.[00]');
    } else {
      return 0;
    }
  },

  tokenClaimFeeId(state: IInventoryState) {
    const claimFee = state.claimFees.find(
      fee => fee.network === state.claimNetwork.network,
    ) as IClaimFee;

    return claimFee.id;
  },
};
